<!-- src/components/TopUpModal.vue -->
<template>
  <div
    class="modal fade"
    :class="{ show: isOpen }"
    :style="{ display: isOpen ? 'block' : 'none' }"
    id="topUpAccountModal"
    data-keyboard="false"
    data-backdrop="static"
    tabindex="-1"
    role="dialog"
    aria-labelledby="topUpAccountModal"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div id="topUpAccount-wrapper">
        <!-- Quick Top Up View -->
        <div v-if="!depositSuccessful" id="quickTopUp">
          <div class="modal-content">
            <div class="modal-header">
              <h4>Quick Top Up</h4>
              <button type="button" class="close" @click="closeModal" aria-label="Close">
                <span aria-hidden="true"><i class="bi bi-x"></i></span>
              </button>
            </div>
            <div class="modal-body">
              <h5>The Bet cannot be placed due to insufficient funds</h5>
              <div class="bal-wrap">
                <!-- CURRENT BALANCE -->
                <div class="available-bal">
                  <div class="text">Available bal</div>
                  <div class="amount">{{ availableBalanceDisplay }} KES</div>
                </div>
                <!-- AMOUNT NEEDED -->
                <div class="needed-bal">
                  <div class="text">Deposit needed</div>
                  <div class="amount">{{ neededAmountDisplay }} KES</div>
                </div>
              </div>

              <!-- PREFILL WITH neededAmountDisplay -->
              <form @submit.prevent="handleDeposit" id="depositForm">
                <div class="mpesa_lg">
                  <img
                    src="https://storage.googleapis.com/kironlite/mossbets/images/safcom_mpesa_lg.png"
                    alt="safaricom logo"
                  />
                </div>
                <div class="form-outline mb-4">
                  <label for="enterAmount">Enter Amount</label>
                  <input
                    type="number"
                    step="1"
                    id="enterAmount"
                    class="form-control form-control-lg"
                    v-model.number="prefillAmount"
                    placeholder="Enter amount"
                    @keypress="preventDecimal"
                  />
                </div>
                <div class="form-outline">
                  <button type="submit" class="btn btn-primary deposit" :disabled="isProcessing">
                    {{ isProcessing ? 'Processing...' : 'Deposit' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <!-- Push Top Up View -->
        <div v-else id="pushTopUp">
          <div class="modal-content">
            <div class="modal-body">
              <div class="successful">
                <img
                  src="https://storage.googleapis.com/kironlite/mossbets/icons/successful.png"
                  alt="Success"
                />
              </div>
              <h4>USSD sent to your Mobile Phone Number</h4>
              <div class="text">
                Once the operation is completed, you can go to your transaction to use your deposit
              </div>
              <form action="#">
                <div class="form-outline" data-dismiss="modal" aria-label="Close">
                  <button type="button" class="btn btn-primary" @click="closeModal">Close</button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <!-- End conditional views -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useDepositStore } from '@/stores/useDepositStore'

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  availableBalance: {
    type: Number,
    default: 0,
  },
  neededAmount: {
    type: Number,
    default: 0,
  },
})

// Emits
const emit = defineEmits(['close', 'deposit-success'])

// Stores
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const depositStore = useDepositStore()

// Reactive state
const depositSuccessful = ref(false)
const isProcessing = ref(false)
const prefillAmount = ref(0)

// Computed properties
const availableBalanceDisplay = computed(() => props.availableBalance.toFixed(2))
const neededAmountDisplay = computed(() => props.neededAmount.toFixed(2))

// Watch for changes in neededAmount and update prefillAmount
watch(
  () => props.neededAmount,
  (newAmount) => {
    prefillAmount.value = newAmount
  },
  { immediate: true },
)

// Methods
function closeModal() {
  emit('close')
}

function preventDecimal(e) {
  if (e.key === '.' || e.key === ',') {
    e.preventDefault()
  }
}

async function handleDeposit() {
  const amount = parseFloat(prefillAmount.value)
  if (!amount || isNaN(amount) || amount < 10) {
    notificationStore.showNotification(
      'Please enter a valid deposit amount (minimum 10 KES).',
      'error',
    )
    return
  }

  if (!Number.isInteger(amount)) {
    notificationStore.showNotification('Please enter a whole-number amount (no decimals).', 'error')
    return
  }

  isProcessing.value = true
  try {
    const result = await depositStore.submitOnlineDeposit(amount)
    if (result.success) {
      depositSuccessful.value = true
      notificationStore.showNotification(result.message, 'success')

      // Refresh user balance
      await authStore.fetchBalance()

      // Emit success event
      emit('deposit-success')
    } else {
      notificationStore.showNotification(result.message, 'error')
    }
  } catch (error) {
    notificationStore.showNotification('Deposit failed. Please try again.', 'error')
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
/* Modal Styles */
.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h4 {
  margin: 0;
  font-weight: 600;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body h5 {
  margin-bottom: 1.5rem;
  text-align: center;
  color: #333;
}

.bal-wrap {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 5px;
}

.available-bal,
.needed-bal {
  text-align: center;
}

.available-bal .text,
.needed-bal .text {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.available-bal .amount,
.needed-bal .amount {
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
}

.mpesa_lg {
  text-align: center;
  margin-bottom: 1rem;
}

.mpesa_lg img {
  max-width: 100px;
}

.form-outline {
  margin-bottom: 1rem;
}

.form-outline label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 0.75rem;
}

.btn {
  width: 100%;
  padding: 0.75rem;
  border-radius: 4px;
  font-weight: 500;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
}

.successful {
  text-align: center;
  margin-bottom: 1rem;
}

.successful img {
  max-width: 80px;
  margin-bottom: 1rem;
}

.text {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #6c757d;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
}

/* Responsive */
@media (max-width: 576px) {
  .bal-wrap {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-body {
    padding: 1rem;
  }
}
</style>
